import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiPhone } = FiIcons;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToHeroPlayButton = () => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const heroSection = document.getElementById('hero');
        if (heroSection) {
          // Look for the play button by its data attribute
          const playButton = heroSection.querySelector('[data-play-button]');
          if (playButton) {
            // Calculate position to show the play button at the bottom of the viewport
            const rect = playButton.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const elementTop = rect.top + scrollTop;
            const offsetTop = elementTop - viewportHeight + rect.height + 100;
            
            window.scrollTo({
              top: Math.max(0, offsetTop),
              behavior: 'smooth'
            });

            // Trigger the play event after scrolling
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('triggerHeroPlay'));
            }, 1000);
          }
        }
      }, 100);
    } else {
      const heroSection = document.getElementById('hero');
      if (heroSection) {
        // Look for the play button by its data attribute
        const playButton = heroSection.querySelector('[data-play-button]');
        if (playButton) {
          // Calculate position to show the play button at the bottom of the viewport
          const rect = playButton.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const elementTop = rect.top + scrollTop;
          const offsetTop = elementTop - viewportHeight + rect.height + 100;
          
          window.scrollTo({
            top: Math.max(0, offsetTop),
            behavior: 'smooth'
          });

          // Trigger the play event after scrolling
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('triggerHeroPlay'));
          }, 1000);
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Scroll to top when navigating to home or products page
    if (path === '/products' || path === '/') {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', action: () => handleNavigation('/') },
    { name: 'Products', action: () => handleNavigation('/products') },
    { name: 'ROI Calculator', action: () => scrollToSection('roi') },
    { name: 'Trust', action: () => scrollToSection('trust') },
    { name: 'Demo', action: () => scrollToSection('demo') }
  ];

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-clean-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div 
            className="flex items-center cursor-pointer"
            onClick={() => handleNavigation('/')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img 
              src="https://mzunalhoitapgthoksjn.supabase.co/storage/v1/object/public/site-assets/Alter%20Logo-02_transparent.PNG" 
              alt="EngageDent AI Logo" 
              className="h-24 w-auto object-contain"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={item.action}
                className={`font-medium transition-colors duration-200 ${
                  isScrolled 
                    ? 'text-slate-gray hover:text-dental-blue' 
                    : 'text-clean-white hover:text-ai-teal'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.name}
              </motion.button>
            ))}
          </nav>

          {/* Desktop CTA Button */}
          <motion.button
            onClick={scrollToHeroPlayButton}
            className="hidden md:block bg-energy-coral text-clean-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-200 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Listen to Donna
          </motion.button>

          {/* Mobile Menu Button */}
          <motion.button
            className={`md:hidden p-2 rounded-lg ${
              isScrolled ? 'text-slate-gray' : 'text-clean-white'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon 
              icon={isMobileMenuOpen ? FiX : FiMenu} 
              className="text-2xl" 
            />
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden bg-clean-white shadow-lg"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 py-4 space-y-4">
              {navItems.map((item) => (
                <motion.button
                  key={item.name}
                  onClick={item.action}
                  className="block w-full text-left font-medium text-slate-gray hover:text-dental-blue transition-colors duration-200"
                  whileTap={{ scale: 0.95 }}
                >
                  {item.name}
                </motion.button>
              ))}
              <motion.button
                onClick={scrollToHeroPlayButton}
                className="w-full bg-energy-coral text-clean-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-200 flex items-center justify-center space-x-2"
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiPhone} className="text-lg" />
                <span>Listen to Donna</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;